import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, CheckCircle, CalendarDays, Layers, 
  Sparkles, Zap, BarChart, UserCheck, ShieldCheck, 
  Clock, ArrowRight, Phone, MessageSquare, TrendingUp,
  BrainCircuit, ChevronRight
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { RefreshCw } from 'lucide-react';

const WORKFLOW_STEPS = [
  { icon: UserCheck, label: "Analyzing Patient History" },
  { icon: ShieldCheck, label: "Verifying Insurance" },
  { icon: Settings, label: "Matching Provider Preferences" },
  { icon: BarChart, label: "Optimizing Chair Utilization" },
  { icon: Layers, label: "Grouping Similar Procedures" },
  { icon: Clock, label: "Predicting Duration" },
];

const ScheduleColumn = ({ title, appointments, isOptimized }) => (
  <div className="flex-1 bg-slate-50/50 rounded-lg border border-slate-100 overflow-hidden flex flex-col h-full relative group">
    <div className="p-2 border-b border-slate-100 bg-slate-50/80 text-center text-xs font-semibold text-slate-600">
      {title}
    </div>
    <div className="flex-1 relative p-1">
      {/* Time markers background */}
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="absolute w-full border-t border-slate-100/60 text-[9px] text-slate-300 pl-1" style={{ top: `${(i / 9) * 100}%` }}>
          {i + 8}:00
        </div>
      ))}
      
      {/* Appointments */}
      <AnimatePresence mode='wait'>
        {appointments.map((apt, index) => (
          <motion.div
            key={`${isOptimized ? 'opt' : 'raw'}-${index}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className={`absolute rounded-[4px] border text-[9px] p-1 flex flex-col justify-center overflow-hidden shadow-sm transition-all hover:scale-[1.02] hover:shadow-md z-10
              ${apt.type === 'crown' ? 'bg-blue-100/90 border-blue-200 text-blue-800' : 
                apt.type === 'hygiene' ? 'bg-green-100/90 border-green-200 text-green-800' : 
                'bg-purple-100/90 border-purple-200 text-purple-800'}`}
            style={{
              top: `${((apt.start - 8) / 9) * 100}%`,
              height: `${(apt.duration / 9) * 100}%`,
              left: '4px',
              right: '4px',
            }}
          >
            <div className="font-bold truncate leading-tight">{apt.patient}</div>
            <div className="opacity-75 truncate leading-tight">{apt.proc}</div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  </div>
);

const LenaSlide = () => {
  const [stage, setStage] = useState('before'); // 'before', 'processing', 'after'

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setStage('before');
        await new Promise(r => setTimeout(r, 3000));
        setStage('processing');
        await new Promise(r => setTimeout(r, 4000));
        setStage('after');
        await new Promise(r => setTimeout(r, 6000));
      }
    };
    cycle();
  }, []);

  // Mock Data
  const rawSchedule = [
    // Op 1
    { col: 0, start: 8, duration: 1, type: 'crown', patient: 'Sarah J.', proc: 'Crown Prep' },
    { col: 0, start: 10.5, duration: 1.5, type: 'other', patient: 'Mike T.', proc: 'Root Canal' },
    { col: 0, start: 15, duration: 1, type: 'crown', patient: 'Emma W.', proc: 'Crown Seat' },
    // Op 2
    { col: 1, start: 9, duration: 1, type: 'hygiene', patient: 'John D.', proc: 'Prophy' },
    { col: 1, start: 13, duration: 1, type: 'hygiene', patient: 'Lisa M.', proc: 'Perio Maint' },
    // Op 3
    { col: 2, start: 8.5, duration: 2, type: 'other', patient: 'Rob K.', proc: 'Bridge Prep' },
    { col: 2, start: 14, duration: 1, type: 'hygiene', patient: 'Amy R.', proc: 'New Patient' },
  ];

  const optimizedSchedule = [
    // Op 1 (High Value / Complex)
    { col: 0, start: 8, duration: 1.5, type: 'crown', patient: 'Sarah J.', proc: 'Crown Prep' },
    { col: 0, start: 9.5, duration: 1.5, type: 'crown', patient: 'New Pt', proc: 'Crown Prep' },
    { col: 0, start: 11, duration: 2, type: 'other', patient: 'Mike T.', proc: 'Root Canal' },
    { col: 0, start: 14, duration: 1, type: 'crown', patient: 'Emma W.', proc: 'Crown Seat' },
    { col: 0, start: 15, duration: 2, type: 'other', patient: 'Rob K.', proc: 'Bridge Prep' },
    // Op 2 (Hygiene 1)
    { col: 1, start: 8, duration: 1, type: 'hygiene', patient: 'Waitlist', proc: 'Prophy' },
    { col: 1, start: 9, duration: 1, type: 'hygiene', patient: 'John D.', proc: 'Prophy' },
    { col: 1, start: 10, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 1, start: 11, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 1, start: 13, duration: 1, type: 'hygiene', patient: 'Lisa M.', proc: 'Perio Maint' },
    { col: 1, start: 14, duration: 1, type: 'hygiene', patient: 'Amy R.', proc: 'New Patient' },
    { col: 1, start: 15, duration: 1, type: 'hygiene', patient: 'Waitlist', proc: 'Prophy' },
    // Op 3 (Hygiene 2 / Overflow)
    { col: 2, start: 8, duration: 1, type: 'hygiene', patient: 'Waitlist', proc: 'Prophy' },
    { col: 2, start: 9, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 2, start: 10, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 2, start: 11, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 2, start: 13, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 2, start: 14, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
    { col: 2, start: 15, duration: 1, type: 'hygiene', patient: 'Fill-in', proc: 'Prophy' },
  ];

  const getAppointmentsForCol = (colIndex, isOpt) => 
    (isOpt ? optimizedSchedule : rawSchedule).filter(a => a.col === colIndex);

  return (
    <div className="min-h-full xl:h-full w-full bg-slate-50 flex flex-col items-center p-4 xl:p-12 relative overflow-y-auto xl:overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
             <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
        </div>

        {/* Floating Agent Header (Top Right) */}
        <div className="absolute top-8 right-8 lg:right-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/leena.png" alt="Leena" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Leena</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Schedule Optimizer</p>
            </div>
        </div>

        {/* Header Section */}
        <div className="relative z-10 w-full max-w-7xl mb-10 flex flex-col xl:flex-row items-start justify-between gap-8 pt-0 mt-20 xl:mt-0 pr-0 xl:pr-64">
            {/* Title & Agent Info */}
            <div className="w-full text-left">
                
                <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                    Runs <span className="text-blue-600">multi-step intelligent AI campaigns</span> to take your schedule from patchy to full.
                </h2>
                
                <p className="text-lg text-slate-600 leading-relaxed w-full">
                    Leena runs 12-step intelligent AI workflows and has intelligent conversations with patients to confirm more appointments <span className="font-bold text-slate-900">than standard SMS campaigns</span> and fill more cancellations <span className="font-bold text-slate-900">than standard SMS</span>.
                </p>
            </div>

            {/* Stats Card - Removed */}
        </div>

        {/* Visualization Grid */}
        <div className="relative z-10 w-full max-w-[1400px] flex-1 min-h-0 pb-8 xl:pb-0">
            {/* Arrow connecting the two states */}
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 transition-all duration-500 hidden xl:block ${stage === 'processing' ? 'opacity-100 scale-110' : 'opacity-40 scale-100'}`}>
                <div className="w-16 h-16 rounded-full bg-white shadow-lg border border-blue-100 flex items-center justify-center">
                    {stage === 'processing' ? (
                        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                    ) : (
                        <ArrowRight className="w-8 h-8 text-slate-300" />
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 h-auto xl:h-full items-stretch">
                
                {/* Left: Before State */}
                <div className="flex flex-col h-[400px] xl:h-full">
                    <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-5 h-full flex flex-col transition-all duration-700 ${stage === 'after' ? 'opacity-60 scale-[0.98]' : 'opacity-100 scale-100 ring-4 ring-slate-100'}`}>
                       <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                 <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                 <span className="font-bold text-slate-700">Before Leena</span>
                            </div>
                            <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                                 Patchy Schedule
                            </Badge>
                       </div>
                       <div className="flex-1 flex gap-2 min-h-0">
                            {[0, 1, 2].map(i => (
                                <ScheduleColumn 
                                    key={i} 
                                    title={`Operatory ${i + 1}`} 
                                    appointments={getAppointmentsForCol(i, false)} 
                                />
                            ))}
                       </div>
                    </div>
                </div>

                {/* Right: After State */}
                <div className="flex flex-col h-[400px] xl:h-full">
                    <div className={`bg-white rounded-2xl shadow-xl border border-blue-200 p-5 h-full flex flex-col transition-all duration-700 relative overflow-hidden ${stage === 'after' ? 'opacity-100 scale-100 ring-4 ring-blue-500/10' : 'opacity-50 scale-[0.98]'}`}>
                       
                       {/* Success sheen effect */}
                       {stage === 'after' && (
                           <motion.div 
                                initial={{ x: '-100%', opacity: 0 }}
                                animate={{ x: '200%', opacity: 0.5 }}
                                transition={{ duration: 1.5, ease: "easeInOut" }}
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 z-20 pointer-events-none"
                           />
                       )}

                       <div className="flex items-center justify-between mb-4 relative z-10">
                            <div className="flex items-center gap-2">
                                 <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                                 <span className="font-bold text-blue-900">After Leena</span>
                            </div>
                            <Badge className="bg-blue-600 text-white hover:bg-blue-700 shadow-sm border-0">
                                 Full Schedule
                            </Badge>
                       </div>
                       
                       <div className="flex-1 flex gap-2 relative z-10 min-h-0">
                            {[0, 1, 2].map(i => (
                                <ScheduleColumn 
                                    key={i} 
                                    title={`Operatory ${i + 1}`} 
                                    appointments={getAppointmentsForCol(i, true)} 
                                    isOptimized={true}
                                />
                            ))}
                       </div>
                       
                       {/* Production Stats */}
                        <div className="relative z-10 mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 flex justify-between items-center border border-blue-100">
                            <div className="text-xs font-semibold text-blue-700">Production Increase</div>
                            <div className="text-lg font-bold text-blue-700 flex items-center gap-1">
                                +28% <TrendingUp className="w-4 h-4" />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
  );
};

export default LenaSlide;
